/**
 * The bright-star catalogue the ENHANCED night sky is drawn from: every star
 * the *Yale Bright Star Catalogue* lists at visual magnitude 4.00 or brighter,
 * which is 518 of its 9,096 entries and very close to what an unaided eye
 * picks out of a dark sky before it starts filling in the gaps.
 *
 * ## Provenance
 *
 * *Bright Star Catalogue*, 5th Revised Edition (Hoffleit & Warren 1991),
 * distributed by the CDS as catalogue **V/50**. Right ascension and
 * declination are its own J2000.0 columns (bytes 76-90 of a record) and the
 * magnitude is its `Vmag` (bytes 103-107); nothing has been interpolated,
 * fitted or invented. It is the standard scientific bright-star list, it is
 * freely redistributable, and it is old enough that the sky it describes is
 * the sky the men in these aeroplanes were looking at.
 *
 * ## Why the data is in source rather than in `data/baked`
 *
 * Everything under `data/baked` is produced from `data/original` by
 * `tools/bake`, and the point of that rule is that a baked file is a
 * *derivation of the game*. This is not: no byte of it comes from Dynamix, and
 * a bake stage for it would have to reach the network or carry a copy of V/50
 * in the repo anyway. So it is embedded here, where it needs no fetch, cannot
 * 404 in a render loop, and is the same 518 stars offline as on.
 *
 * ## The shape
 *
 * One flat number array, three values a star: right ascension in **degrees**
 * (not the catalogue's hours — this port has no other use for hours), then
 * declination in degrees, then magnitude. Sorted brightest first, so
 * `BRIGHT_STAR_LANDMARKS` can name a star by index and a truncation of the
 * table would drop the faint end rather than a random third of the sky.
 *
 * Positions are J2000.0 and are precessed to the mission's own date by
 * `sky-astronomy.ts`'s `precessFromJ2000` — 84 years is about 1.2 degrees of
 * general precession and rather more than that near the pole, which is the
 * difference between Polaris being *the* pole star and being a degree and a
 * half off it.
 */

/** One catalogue entry, unpacked. */
export interface CatalogueStar {
  /** J2000.0 right ascension, degrees. */
  readonly rightAscensionDeg: number;
  /** J2000.0 declination, degrees. */
  readonly declinationDeg: number;
  /** Johnson V magnitude — smaller is brighter, and Sirius is negative. */
  readonly magnitude: number;
}

/** The dimmest star in the table. Everything fainter than this was left in the catalogue. */
export const CATALOGUE_MAGNITUDE_LIMIT = 4;

const VALUES_PER_STAR = 3;

/** `raDeg, decDeg, mag` per star, brightest first. See the module doc for the source. */
const BRIGHT_STARS: readonly number[] = [
  101.287, -16.716, -1.46, 95.988, -52.696, -0.72, 213.915, 19.183, -0.04, 219.9, -60.835, -0.01, 279.235,
  38.784, 0.03, 79.172, 45.998, 0.08, 78.635, -8.202, 0.12, 114.825, 5.225, 0.38, 24.429, -57.237, 0.46,
  88.793, 7.407, 0.5, 210.956, -60.373, 0.61, 297.696, 8.868, 0.77, 68.98, 16.509, 0.85, 247.352, -26.432,
  0.96, 201.298, -11.161, 0.98, 116.329, 28.026, 1.14, 344.413, -29.622, 1.16, 191.93, -59.689, 1.25, 310.358,
  45.28, 1.25, 186.65, -63.099, 1.33, 219.9, -60.836, 1.33, 152.093, 11.967, 1.35, 104.656, -28.972, 1.5,
  187.791, -57.113, 1.63, 263.402, -37.104, 1.63, 81.283, 6.35, 1.64, 81.573, 28.608, 1.65, 138.3, -69.717,
  1.68, 84.053, -1.202, 1.7, 186.652, -63.099, 1.73, 332.058, -46.961, 1.74, 193.507, 55.96, 1.77, 122.383,
  -47.337, 1.78, 51.081, 49.861, 1.79, 165.932, 61.751, 1.79, 107.098, -26.393, 1.84, 276.043, -34.385, 1.85,
  125.628, -59.51, 1.86, 206.885, 49.313, 1.86, 264.33, -42.998, 1.87, 89.882, 44.947, 1.9, 252.166, -69.028,
  1.92, 99.428, 16.399, 1.93, 306.412, -56.735, 1.94, 131.176, -54.708, 1.96, 95.675, -17.956, 1.98, 113.65,
  31.888, 1.98, 141.897, -8.659, 1.98, 31.793, 23.462, 2, 239.876, 25.92, 2, 37.953, 89.264, 2.02, 283.816,
  -26.297, 2.02, 10.898, -17.987, 2.04, 85.19, -1.943, 2.05, 2.097, 29.091, 2.06, 17.433, 35.621, 2.06, 86.939,
  -9.67, 2.06, 211.671, -36.37, 2.06, 222.676, 74.156, 2.08, 263.734, 12.56, 2.08, 340.667, -46.885, 2.1,
  47.042, 40.956, 2.12, 177.265, 14.572, 2.14, 190.379, -48.96, 2.17, 305.557, 40.257, 2.2, 136.999, -43.432,
  2.21, 10.127, 56.537, 2.23, 83.002, -0.299, 2.23, 233.672, 26.715, 2.23, 269.152, 51.489, 2.23, 120.896,
  -40.003, 2.25, 139.273, -59.275, 2.25, 30.975, 42.33, 2.26, 2.295, 59.15, 2.27, 200.981, 54.925, 2.27,
  252.541, -34.293, 2.29, 204.972, -53.466, 2.3, 220.482, -47.388, 2.3, 218.877, -42.158, 2.31, 240.083,
  -22.622, 2.32, 165.46, 56.383, 2.37, 6.571, -42.306, 2.39, 326.047, 9.875, 2.39, 265.622, -39.03, 2.41,
  345.944, 28.083, 2.42, 257.595, -15.725, 2.43, 178.457, 53.695, 2.44, 319.645, 62.586, 2.44, 111.024,
  -29.303, 2.45, 311.553, 33.97, 2.46, 14.177, 60.717, 2.47, 346.19, 15.205, 2.49, 140.528, -55.011, 2.5,
  45.57, 4.09, 2.53, 208.885, -47.288, 2.55, 168.527, 20.524, 2.56, 249.29, -10.567, 2.56, 83.182, -17.822,
  2.58, 183.952, -17.542, 2.59, 182.09, -50.723, 2.6, 285.653, -29.88, 2.6, 154.993, 19.842, 2.61, 229.252,
  -9.383, 2.61, 89.93, 37.213, 2.62, 241.359, -19.806, 2.62, 28.66, 20.808, 2.64, 84.912, -34.074, 2.64,
  188.597, -23.397, 2.65, 236.067, 6.426, 2.65, 21.454, 60.235, 2.68, 208.671, 18.398, 2.68, 224.633, -43.134,
  2.68, 74.248, 33.166, 2.69, 161.692, -49.42, 2.69, 189.296, -69.136, 2.69, 262.691, -37.296, 2.69, 109.286,
  -37.098, 2.7, 221.247, 27.074, 2.7, 275.249, -29.828, 2.7, 296.565, 10.613, 2.72, 243.586, -3.694, 2.74,
  245.998, 61.514, 2.74, 200.149, -36.712, 2.75, 222.72, -16.042, 2.75, 160.739, -64.394, 2.76, 83.858, -5.91,
  2.77, 247.555, 21.49, 2.77, 265.868, 4.567, 2.77, 233.785, -41.167, 2.78, 76.963, -5.086, 2.79, 262.608,
  52.301, 2.79, 6.438, -77.254, 2.8, 183.786, -58.749, 2.8, 121.886, -24.304, 2.81, 250.322, 31.603, 2.81,
  276.993, -25.422, 2.81, 248.971, -28.216, 2.82, 3.309, 15.184, 2.83, 195.544, 10.959, 2.83, 82.061, -20.759,
  2.84, 58.533, 31.884, 2.85, 238.785, -63.431, 2.85, 261.325, -55.53, 2.85, 29.692, -61.57, 2.86, 334.625,
  -60.26, 2.86, 56.871, 24.105, 2.87, 296.244, 45.131, 2.87, 326.76, -16.127, 2.87, 95.74, 22.514, 2.88,
  113.65, 31.889, 2.88, 59.463, 40.01, 2.89, 229.728, -68.679, 2.89, 239.713, -26.114, 2.89, 245.297, -25.593,
  2.89, 287.441, -21.024, 2.89, 111.788, 8.289, 2.9, 194.007, 38.318, 2.9, 322.89, -5.571, 2.91, 46.199,
  53.506, 2.93, 102.484, -50.615, 2.93, 340.75, 30.221, 2.94, 59.508, -13.509, 2.95, 187.466, -16.516, 2.95,
  262.96, -49.876, 2.95, 331.446, -0.32, 2.96, 100.983, 25.131, 2.98, 146.463, 23.774, 2.98, 75.492, 43.823,
  2.99, 271.452, -30.424, 2.99, 286.352, 13.863, 2.99, 32.386, 34.987, 3, 84.411, 21.142, 3, 182.531, -22.62,
  3, 199.73, -23.172, 3, 55.731, 47.788, 3.01, 146.775, -65.072, 3.01, 167.416, 44.499, 3.01, 328.482, -37.365,
  3.01, 95.078, -30.063, 3.02, 105.756, -23.833, 3.02, 218.02, 38.308, 3.03, 266.896, -40.127, 3.03, 34.836,
  -2.978, 3.04, 207.404, -42.474, 3.04, 155.582, 41.499, 3.05, 191.57, -68.108, 3.05, 230.182, 71.834, 3.05,
  288.139, 67.662, 3.07, 252.968, -38.047, 3.08, 292.68, 27.96, 3.08, 305.253, -14.781, 3.08, 133.848, 5.946,
  3.11, 162.406, -16.194, 3.11, 274.407, -36.762, 3.11, 309.392, -47.291, 3.11, 87.74, -35.768, 3.12, 140.264,
  34.392, 3.13, 142.805, -57.034, 3.13, 173.945, -63.02, 3.13, 224.79, -42.104, 3.13, 254.655, -55.99, 3.13,
  134.802, 48.042, 3.14, 258.758, 24.839, 3.14, 258.762, 36.809, 3.16, 76.629, 41.234, 3.17, 99.44, -43.196,
  3.17, 143.214, 51.677, 3.17, 257.197, 65.715, 3.17, 281.414, -26.991, 3.17, 72.46, 6.961, 3.19, 76.365,
  -22.371, 3.19, 220.627, -64.975, 3.19, 254.417, 9.375, 3.2, 318.234, 30.227, 3.2, 267.465, -37.043, 3.21,
  354.837, 77.632, 3.21, 230.343, -40.648, 3.22, 302.826, -0.821, 3.23, 322.165, 70.561, 3.23, 44.565, -40.305,
  3.24, 56.81, -74.239, 3.24, 244.58, -4.692, 3.24, 284.736, 32.689, 3.24, 112.308, -43.301, 3.25, 275.328,
  -2.899, 3.26, 9.832, 30.861, 3.27, 68.499, -55.045, 3.27, 102.047, -61.941, 3.27, 211.593, -26.683, 3.27,
  260.502, -24.999, 3.27, 343.662, -15.821, 3.27, 93.719, 22.507, 3.28, 226.017, -25.282, 3.29, 231.232,
  58.966, 3.29, 16.521, -46.719, 3.31, 78.233, -16.206, 3.31, 183.857, 57.032, 3.31, 153.434, -70.038, 3.32,
  158.006, -61.685, 3.32, 286.735, -27.671, 3.32, 258.038, -43.239, 3.33, 117.324, -24.86, 3.34, 168.56,
  15.429, 3.34, 261.348, -56.377, 3.34, 269.757, -9.774, 3.34, 63.606, -62.474, 3.35, 332.714, 58.201, 3.35,
  81.119, -2.397, 3.36, 101.322, 12.896, 3.36, 127.566, 60.718, 3.36, 291.375, 3.115, 3.36, 203.673, -0.596,
  3.37, 230.67, -44.689, 3.37, 28.599, 63.67, 3.38, 131.694, 6.419, 3.38, 193.901, 3.397, 3.38, 46.294, 38.84,
  3.39, 67.165, 15.871, 3.4, 154.271, -61.332, 3.4, 340.365, 10.831, 3.4, 22.091, -43.318, 3.41, 28.27, 29.579,
  3.41, 207.376, -41.688, 3.41, 228.071, -52.099, 3.41, 240.03, -38.397, 3.41, 266.615, 27.721, 3.42, 311.24,
  -66.203, 3.42, 311.322, 61.839, 3.43, 12.275, 57.816, 3.44, 137.742, -58.967, 3.44, 154.173, 23.417, 3.44,
  286.562, -4.883, 3.44, 17.148, -10.182, 3.45, 154.274, 42.914, 3.45, 282.52, 33.363, 3.45, 40.825, 3.236,
  3.47, 60.17, 12.49, 3.47, 105.43, -27.935, 3.47, 119.195, -52.982, 3.47, 228.876, 33.315, 3.47, 299.689,
  19.492, 3.47, 169.62, 33.094, 3.48, 258.662, 14.39, 3.48, 342.501, 24.602, 3.48, 342.139, -51.317, 3.49,
  26.017, -15.938, 3.5, 225.487, 40.391, 3.5, 276.743, -45.968, 3.51, 284.432, -21.107, 3.51, 124.129, 9.186,
  3.52, 145.287, 9.892, 3.52, 151.833, 16.763, 3.52, 342.42, 66.201, 3.52, 67.154, 19.18, 3.53, 110.031,
  21.982, 3.53, 237.405, -3.43, 3.53, 250.724, 38.922, 3.53, 332.55, 6.198, 3.53, 55.812, -9.763, 3.54, 83.785,
  9.934, 3.54, 149.216, -54.568, 3.54, 173.25, -31.858, 3.54, 264.397, -15.399, 3.54, 86.739, -14.822, 3.55,
  214.851, -46.058, 3.55, 4.857, -8.824, 3.56, 34.128, -51.512, 3.56, 64.474, -33.798, 3.56, 169.835, -14.779,
  3.56, 230.452, -36.261, 3.56, 302.182, -66.182, 3.56, 24.498, 48.628, 3.57, 116.112, 24.398, 3.57, 253.084,
  -38.017, 3.57, 275.264, 72.733, 3.57, 304.514, -12.545, 3.57, 109.523, 16.54, 3.58, 217.958, 30.371, 3.58,
  234.256, -28.135, 3.58, 185.34, -60.401, 3.59, 21.006, -8.183, 3.6, 51.203, 9.029, 3.6, 79.402, -6.844, 3.6,
  86.116, -22.448, 3.6, 103.197, 33.961, 3.6, 135.906, 47.157, 3.6, 142.675, -40.467, 3.6, 116.314, -37.969,
  3.61, 152.647, -12.354, 3.61, 177.674, 1.765, 3.61, 22.871, 15.346, 3.62, 130.073, -52.922, 3.62, 195.567,
  -71.549, 3.62, 253.646, -42.361, 3.62, 262.775, -60.684, 3.62, 266.433, -64.724, 3.62, 345.48, 42.326, 3.62,
  42.496, 27.261, 3.63, 57.29, 24.053, 3.63, 309.387, 14.595, 3.63, 176.402, -66.729, 3.64, 64.948, 15.628,
  3.65, 190.415, -1.449, 3.65, 211.097, 64.376, 3.65, 313.702, -58.454, 3.65, 9.243, 53.897, 3.66, 234.664,
  -29.778, 3.66, 271.658, -50.092, 3.66, 347.362, -21.172, 3.66, 142.882, 63.062, 3.67, 236.547, 15.422, 3.67,
  130.898, -33.186, 3.68, 190.415, -1.449, 3.68, 231.957, 29.106, 3.68, 325.023, -16.662, 3.68, 49.879,
  -21.758, 3.69, 72.802, 5.605, 3.69, 146.312, -62.508, 3.69, 349.291, 3.282, 3.69, 28.99, -51.609, 3.7,
  56.219, 24.113, 3.7, 269.441, 29.248, 3.7, 89.101, -14.168, 3.71, 176.512, 47.779, 3.71, 237.704, 4.478,
  3.71, 298.828, 6.407, 3.71, 73.563, 2.441, 3.72, 89.882, 54.285, 3.72, 221.562, 1.893, 3.72, 316.233, 43.928,
  3.72, 318.698, 38.046, 3.72, 27.865, -10.335, 3.73, 53.232, -9.458, 3.73, 118.054, -40.576, 3.73, 271.837,
  9.564, 3.73, 51.793, 9.733, 3.74, 321.667, -22.411, 3.74, 343.154, -7.58, 3.74, 75.62, 41.076, 3.75, 136.039,
  -47.098, 3.75, 245.48, 19.153, 3.75, 266.973, 2.707, 3.75, 268.382, 56.873, 3.75, 337.293, 58.415, 3.75,
  42.674, 55.896, 3.76, 65.734, 17.543, 3.76, 83.406, -62.49, 3.76, 252.446, -59.041, 3.76, 325.369, -77.39,
  3.76, 331.753, 25.345, 3.76, 56.298, 42.579, 3.77, 126.434, -66.137, 3.77, 286.171, -21.742, 3.77, 289.276,
  53.369, 3.77, 309.91, 15.912, 3.77, 311.919, -9.496, 3.77, 337.823, 50.282, 3.77, 107.187, -70.499, 3.78,
  163.373, -58.853, 3.78, 106.027, 20.57, 3.79, 111.432, 27.798, 3.79, 292.426, 51.73, 3.79, 303.408, 46.741,
  3.79, 47.374, 44.857, 3.8, 147.748, 59.039, 3.8, 154.994, 19.841, 3.8, 233.7, 10.537, 3.8, 233.7, 10.539,
  3.8, 264.866, 46.006, 3.8, 84.687, -2.6, 3.81, 87.83, -20.879, 3.81, 156.523, -16.836, 3.81, 68.887, -30.562,
  3.82, 139.711, 36.802, 3.82, 156.97, -58.739, 3.82, 247.728, 1.984, 3.82, 296.847, 18.534, 3.82, 354.391,
  46.458, 3.82, 56.08, 32.288, 3.83, 163.328, 34.215, 3.83, 209.568, -42.101, 3.83, 221.965, -79.045, 3.83,
  271.886, 28.762, 3.83, 297.043, 70.268, 3.83, 67.144, 15.962, 3.84, 130.157, -46.649, 3.84, 133.762, -60.645,
  3.84, 159.325, -48.226, 3.84, 172.851, 69.331, 3.84, 235.686, 26.296, 3.84, 275.925, 21.77, 3.84, 335.414,
  -1.387, 3.84, 56.05, -64.807, 3.85, 86.821, -51.066, 3.85, 95.528, -33.436, 3.85, 108.703, -26.773, 3.85,
  153.684, -42.122, 3.85, 158.203, 9.307, 3.85, 239.113, 15.662, 3.85, 243.86, -63.686, 3.85, 278.802, -8.244,
  3.85, 63.5, -42.294, 3.86, 189.426, -48.541, 3.86, 269.063, 37.251, 3.86, 273.441, -21.059, 3.86, 14.188,
  38.499, 3.87, 48.018, -28.987, 3.87, 56.457, 24.368, 3.87, 69.545, -14.304, 3.87, 82.803, -35.471, 3.87,
  103.533, -24.184, 3.87, 188.117, -72.133, 3.87, 188.371, 69.788, 3.87, 209.67, -44.804, 3.87, 227.984,
  -48.738, 3.87, 2.353, -45.748, 3.88, 138.591, 2.314, 3.88, 148.191, 26.007, 3.88, 202.761, -39.407, 3.88,
  220.765, -5.658, 3.88, 239.221, -29.214, 3.88, 44.107, -8.898, 3.89, 170.252, -54.491, 3.89, 184.977, -0.667,
  3.89, 244.935, 46.313, 3.89, 248.363, -78.897, 3.89, 299.077, 35.083, 3.89, 126.415, -3.906, 3.9, 298.118,
  1.006, 3.9, 347.59, -45.247, 3.9, 60.789, 5.989, 3.91, 131.507, -46.042, 3.91, 144.964, -1.143, 3.91,
  167.147, -58.975, 3.91, 187.01, -50.231, 3.91, 233.882, -14.789, 3.91, 17.096, -55.246, 3.92, 255.073,
  30.926, 3.92, 318.956, 5.248, 3.92, 69.08, -3.353, 3.93, 115.312, -9.551, 3.93, 290.418, -17.847, 3.93,
  6.551, -43.68, 3.94, 131.171, 18.154, 3.94, 170.981, 10.529, 3.94, 314.293, 41.167, 3.94, 22.813, -49.073,
  3.95, 43.565, 52.763, 3.95, 99.171, -19.256, 3.95, 115.455, -72.606, 3.95, 200.985, 54.922, 3.95, 237.74,
  -33.627, 3.95, 341.633, 23.566, 3.95, 66.009, -34.017, 3.96, 89.787, -42.815, 3.96, 102.46, -32.509, 3.96,
  115.952, -28.955, 3.96, 182.913, -52.369, 3.96, 241.702, -20.669, 3.96, 300.148, -72.911, 3.96, 87.873,
  39.149, 3.97, 130.026, -35.308, 3.97, 135.16, 41.783, 3.97, 137.819, -62.317, 3.97, 270.161, 2.932, 3.97,
  290.972, -40.616, 3.97, 337.317, -43.496, 3.97, 350.743, -20.101, 3.97, 30.859, 72.421, 3.98, 93.714, -6.275,
  3.98, 109.207, -67.957, 3.98, 303.868, 47.714, 3.98, 349.358, -58.236, 3.99, 30.001, -21.078, 4, 43.47,
  -49.89, 4, 135.612, -66.396, 4, 156.099, -74.032, 4, 220.49, -37.794, 4,
];

export const BRIGHT_STAR_COUNT = BRIGHT_STARS.length / VALUES_PER_STAR;

/** One star by index, or `undefined` past the end of the table. */
export function brightStarAt(index: number): CatalogueStar | undefined {
  const at = index * VALUES_PER_STAR;
  const rightAscensionDeg = BRIGHT_STARS[at];
  const declinationDeg = BRIGHT_STARS[at + 1];
  const magnitude = BRIGHT_STARS[at + 2];
  if (rightAscensionDeg === undefined || declinationDeg === undefined || magnitude === undefined) {
    return undefined;
  }
  return { rightAscensionDeg, declinationDeg, magnitude };
}

/** The whole table, unpacked once and reused — the star field rebuilds on every re-dress. */
let unpacked: readonly CatalogueStar[] | undefined;

export function brightStars(): readonly CatalogueStar[] {
  if (unpacked) return unpacked;
  const stars: CatalogueStar[] = [];
  for (let index = 0; index < BRIGHT_STAR_COUNT; index += 1) {
    const star = brightStarAt(index);
    if (star) stars.push(star);
  }
  unpacked = stars;
  return unpacked;
}

/**
 * Indices of the stars a person can actually check a screenshot against: the
 * seven of the Plough, Orion's seven, and the dozen or so first-magnitude
 * stars everybody knows by name.
 *
 * This exists for `night-sky-look.test.ts`, not for the renderer — "is Orion
 * upright and in the south-east at 3 a.m. in December" is the only assertion
 * that can tell a working star field from a plausible-looking bug, and it
 * needs to be able to say *which* star.
 */
export const BRIGHT_STAR_LANDMARKS: Readonly<Record<string, number>> = {
  Dubhe: 34,
  Merak: 80,
  Phecda: 86,
  Megrez: 236,
  Alioth: 31,
  Mizar: 74,
  Alkaid: 38,
  Betelgeuse: 9,
  Rigel: 6,
  Bellatrix: 25,
  Mintaka: 67,
  Alnilam: 28,
  Alnitak: 53,
  Saiph: 56,
  Sirius: 0,
  Polaris: 50,
  Vega: 4,
  Deneb: 18,
  Altair: 11,
  Arcturus: 2,
  Capella: 5,
  Aldebaran: 12,
  Antares: 13,
  Spica: 14,
  Procyon: 7,
  Pollux: 15,
  Castor: 46,
  Regulus: 21,
  Fomalhaut: 16,
};
